import { useLocation } from "react-router-dom";
import { useSettings } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { AlertTriangle } from "lucide-react";

/**
 * Zero-flash maintenance gate.
 *  - Renders a neutral dark loading surface while settings are being resolved.
 *  - Blocks the entire storefront with a full-screen maintenance page when maintenance is active
 *    (except on /login, /register and /admin/* routes so admins can still get in).
 *  - Admins/system admins always bypass.
 */
export default function AppGate({ children }) {
  const { ready, notices = [] } = useSettings();
  const { user } = useAuth();
  const location = useLocation();
  const path = location.pathname || "";

  const authRoute = path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/admin");
  const isAdmin = user && user.role === "admin";

  // Still resolving user (undefined) OR settings — hold storefront mount to avoid flash.
  // Skip the loading gate on auth/admin routes so admins can log in immediately.
  const authResolved = user !== undefined; // undefined = auth loading
  if (!authRoute && (!ready || !authResolved)) {
    return (
      <div data-testid="app-gate-loading" aria-hidden="true"
        className="fixed inset-0 z-[100] bg-[#191A1D]" />
    );
  }

  // Maintenance active? Only for non-admin on public routes.
  const maintenance = notices.find((n) => n.kind === "maintenance");
  if (maintenance && !authRoute && !isAdmin) {
    // Prevent background scroll
    if (typeof document !== "undefined") {
      document.body.style.overflow = "hidden";
    }
    return (
      <div data-testid="maintenance-screen" className="fixed inset-0 z-[80] bg-[#0f1012] flex items-center justify-center p-4 overflow-hidden">
        <div className="absolute inset-0 pg-grid-lines opacity-30" />
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-[#EF1B17]/20 blur-3xl pointer-events-none" />
        <div className="relative text-center max-w-lg">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#EF1B17]/15 text-[#EF1B17] mb-6 ring-4 ring-[#EF1B17]/20">
            <AlertTriangle size={40} />
          </div>
          <div className="text-[#EF1B17] text-xs font-bold uppercase tracking-[0.3em] mb-3">وضع الصيانة</div>
          <h1 className="text-3xl md:text-4xl font-black text-[#F4F4F2] mb-4">
            {maintenance.title || "الموقع تحت الصيانة"}
          </h1>
          <p className="text-gray-300 whitespace-pre-wrap leading-relaxed mb-6">
            {maintenance.body || "نحن نقوم بتحسين تجربتك حالياً. سنعود قريباً — شكراً لصبرك."}
          </p>
        </div>
      </div>
    );
  }

  // Restore scroll if it was locked before
  if (typeof document !== "undefined") {
    document.body.style.overflow = "";
  }

  return children;
}
